import { FC, useEffect, useMemo } from 'react'
import { Label, ModalFooter } from '@cargomart/ui'
import {
  compose,
  FieldFormItem,
  FieldMobilePhone,
  Form,
  phoneNumberValid,
  required,
  alphabetSymbolsAllow,
  maxChar
} from 'forms'
import { useIsMobile } from 'hooks'
import { prepareContactUpdateData, useLazyGetContactByIdQuery } from 'modules/contact/api'
import { TContact } from 'modules/contact/types'
import { Button } from 'shared/components'
import { FieldText } from 'shared/forms'
import { handleFetchResponse } from 'src/api/errors'
import { PlaceholderCell } from 'src/tailwind-components/extends/PlaceholderCell'

import { contactDetailUpdateRoute } from '../../routes'
import { FieldPhoneExt } from '../ContactCreateForm/ContactCreateForm'
import { ContactUpdateFormProps } from './types'

const ContactUpdateForm: FC<ContactUpdateFormProps> = (props) => {
  const { isUpdating, updateContact, refetch, onClose, onComplete } = props
  const isMobile = useIsMobile()
  const { id } = contactDetailUpdateRoute.useParams()

  const [getContactById, { data: contactById, isLoading: isContactByIdLoading, isFetching }] =
    useLazyGetContactByIdQuery()

  useEffect(() => {
    getContactById(id || '')
  }, []) // eslint-disable-line

  const initialValues = useMemo(
    () => ({
      id: contactById?.id as string,
      lastname: contactById?.lastname as string,
      firstname: contactById?.firstname as string,
      secondname: contactById?.secondname as string,
      phone: contactById?.phone as string,
      phoneExt: contactById?.phoneExt as string
    }),
    [contactById]
  )

  const handleSubmit = (payload: TContact) => {
    if (!updateContact) return

    return updateContact(prepareContactUpdateData(payload)).then(
      handleFetchResponse(() => {
        onClose()
        onComplete()
        refetch && refetch()

        return undefined
      })
    )
  }

  return (
    <Form
      keepDirtyOnReinitialize
      initialValues={initialValues}
      name="id"
      onSubmit={handleSubmit}
      render={(form) => (
        <form onSubmit={form.handleSubmit}>
          <div className="grid grid-cols-3 justify-between gap-2">
            <FieldFormItem label={<Label className="pt-2">Фамилия</Label>} name="lastname">
              {isFetching || isUpdating ? (
                <PlaceholderCell height={32} />
              ) : (
                <FieldText
                  className="[&_svg]:hidden"
                  disabled={isUpdating}
                  name="lastname"
                  testId="contacts-page--form--lastname-input"
                  validate={compose(required, alphabetSymbolsAllow, maxChar(100))}
                />
              )}
            </FieldFormItem>
            <FieldFormItem label={<Label className="pt-2">Имя</Label>} name="firstname">
              {isFetching || isUpdating ? (
                <PlaceholderCell height={32} />
              ) : (
                <FieldText
                  className="[&_svg]:hidden"
                  disabled={isUpdating}
                  name="firstname"
                  testId="contacts-page--form--firstname-input"
                  validate={compose(required, alphabetSymbolsAllow, maxChar(100))}
                />
              )}
            </FieldFormItem>
            <FieldFormItem label={<Label className="pt-2">Отчество</Label>} name="secondname">
              {isFetching || isUpdating ? (
                <PlaceholderCell height={32} />
              ) : (
                <FieldText
                  disabled={isUpdating}
                  name="secondname"
                  placeholder="При наличии"
                  testId="contacts-page--form--secondname-input"
                  validate={compose(alphabetSymbolsAllow, maxChar(100))}
                />
              )}
            </FieldFormItem>
          </div>
          <div className="grid grid-cols-2 justify-between gap-2">
            <FieldFormItem label={<Label className="pt-2">Телефон</Label>} name="phone">
              {isFetching || isUpdating ? (
                <PlaceholderCell height={32} />
              ) : (
                <FieldMobilePhone
                  className="!h-8"
                  disabled={isUpdating}
                  maskType="city"
                  name="phone"
                  size="lg"
                  testId="contacts-page--form--phone-input"
                  validate={compose(required, phoneNumberValid)}
                />
              )}
            </FieldFormItem>
            <FieldPhoneExt isCreating={false} isLoading={isUpdating} />
          </div>
          <ModalFooter className="flex justify-end gap-2 !px-0 !py-6">
            {!isMobile && (
              <Button onClick={onClose} size="lg">
                Отменить
              </Button>
            )}
            <Button
              loading={isUpdating || isContactByIdLoading}
              size="lg"
              type="submit"
              variant="solid"
              data-test-id="contacts-page--contact-update--save-btn"
            >
              Сохранить
            </Button>
          </ModalFooter>
        </form>
      )}
    />
  )
}

export default ContactUpdateForm
